import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Film, Clapperboard, Image as ImageIcon } from 'lucide-react';
import { platformLabel } from '@/lib/clipPlatformFormats';

const KIND_ICON = { video: Film, clip: Clapperboard, design: ImageIcon };

/**
 * Schedule → Content Queue (section 5): every approved-and-unscheduled
 * Optimize variant, grouped one card per content asset with all of its
 * ready platforms. Selecting a platform chip either starts a one-off
 * schedule for it, or — when multi-select mode is on — adds it to the
 * current batch selection.
 */
export function ScheduleQueuePanel({ groups, onScheduleOne, batchMode, selectedBatchKeys, onToggleBatchItem }) {
  if (groups.length === 0) {
    return (
      <div className="text-center py-8 space-y-2" data-testid="schedule-queue-empty">
        <p className="text-sm text-[#8A93A6]">No approved content waiting to be scheduled.</p>
        <p className="text-xs text-[#555E6E]">Approve a platform variant in Optimize and it'll show up here. The calendar stays usable either way.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5" data-testid="schedule-queue-panel">
      {groups.map((g) => {
        const Icon = KIND_ICON[g.contentKind] || Film;
        return (
          <div key={g.assetId} className="p-2.5 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={`schedule-queue-group-${g.assetId}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <Icon className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />
              <span className="text-xs text-white truncate flex-1">{g.contentTitle}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {g.variants.map((v) => {
                const key = `${v.id}`;
                const platform = v.platforms[0];
                return batchMode ? (
                  <label key={key} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-white/10 cursor-pointer hover:bg-white/5" data-testid={`schedule-queue-batch-toggle-${key}`}>
                    <Checkbox checked={selectedBatchKeys.has(key)} onCheckedChange={() => onToggleBatchItem(v, platform)} className="h-3 w-3" />
                    {platformLabel(platform)}
                  </label>
                ) : (
                  <button
                    key={key} draggable onDragStart={(e) => e.dataTransfer.setData('application/x-schedule-queue-item', JSON.stringify({ variantId: v.id, platform }))}
                    onClick={() => onScheduleOne(v, platform)}
                    className="text-[11px] px-2 py-1 rounded-full border border-white/10 text-[#C0C7D4] hover:text-white hover:border-[#3A7BFF]/50"
                    data-testid={`schedule-queue-item-${key}`}
                  >
                    {platformLabel(platform)}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
